import type { SxProps, Theme } from '@mui/material';
import { Box, keyframes } from '@mui/material';
import type { ReactNode } from 'react';
import { MOTION_DURATION_MS, MOTION_EASING, withReducedMotion } from '../../../theme/motion';
import type { WizardDirection } from '../hooks/wizard-steps';

const STEP_ENTRANCE_OFFSET_PX = 24;

// The entrance settles on `transform: none`, not `translateX(0)`: with fill-mode
// `both` the last keyframe stays applied, and any transform would keep turning
// this box into the containing block of fixed descendants (the items-step FAB).
function createStepEntrance(offsetPx: number) {
  return keyframes`
    from {
      opacity: 0;
      transform: translateX(${offsetPx}px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  `;
}

const stepFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const STEP_ENTRANCE_KEYFRAMES = {
  forward: createStepEntrance(STEP_ENTRANCE_OFFSET_PX),
  backward: createStepEntrance(-STEP_ENTRANCE_OFFSET_PX),
} as const;

const STEP_ENTRANCE_TIMING = `${MOTION_DURATION_MS.routine}ms ${MOTION_EASING.arrive} both`;

function getWizardStepTransitionSx(direction: WizardDirection): SxProps<Theme> {
  if (direction === 'none') {
    return {};
  }
  return withReducedMotion({
    animation: `${STEP_ENTRANCE_KEYFRAMES[direction]} ${STEP_ENTRANCE_TIMING}`,
    // While translated, this box re-anchors `position: fixed` descendants to
    // itself: a step that renders a fixed FAB (items) only fades, so the
    // button never jumps mid-entrance.
    // Own reduced-motion guard: this selector outranks the top-level media query.
    '&:has(.MuiFab-root)': withReducedMotion({
      animation: `${stepFadeIn} ${STEP_ENTRANCE_TIMING}`,
    }),
  });
}

interface WizardStepTransitionProps {
  direction: WizardDirection;
  children: ReactNode;
}

export function WizardStepTransition({ direction, children }: WizardStepTransitionProps) {
  return <Box sx={getWizardStepTransitionSx(direction)}>{children}</Box>;
}
